import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useClient } from '@utils/api-client';
import {
  CreateNamedInsuredUpdateResult as ApiCreateNamedInsuredUpdateResult,
  CustomerNamedInsuredUpdateRequestFeasibilityResult as ApiNamedInsuredFeasibilityResult,
  CustomerNamedInsuredUpdateRequestFeasibilityWarning,
} from '@lio/api-types';
import { quoteQueryKey } from '@services/quotes/keys';
import { quoteItemQueryKey } from '@services/quoteItem';
import { QuoteStoreContext } from '@store/Quote/QuoteStore';
import type { QuoteItemSearchResponse } from '@store/Quote/Quote.types';
import { CodeName } from '@models/quoteItem';
import { customerQueryKey } from './keys';

export type NamedInsuredFeasibilityWarning = CustomerNamedInsuredUpdateRequestFeasibilityWarning;
export type NamedInsuredFeasibilityResult = ApiNamedInsuredFeasibilityResult & {
  mdmMatchFound?: boolean;
};
export type CreateNamedInsuredUpdateResult = ApiCreateNamedInsuredUpdateResult & {
  supportTicketCreated?: boolean;
};

export const useCheckNamedInsuredFeasibility = (customerId: string) => {
  const client = useClient();

  const checkFeasibility = async (newName: string): Promise<NamedInsuredFeasibilityResult> => {
    const query = new URLSearchParams({ newName });
    return client
      .get(`customer/${customerId}/named-insured-update/feasibility?${query}`)
      .json<NamedInsuredFeasibilityResult>();
  };

  return { checkFeasibility };
};

export const useCreateNamedInsuredUpdate = () => {
  const client = useClient();
  const queryClient = useQueryClient();
  const quoteStore = useContext(QuoteStoreContext);

  const mutation = useMutation({
    mutationFn: ({
      customerId,
      newName,
      forceJiraTicket,
      quoteId,
    }: {
      customerId: string;
      newName: string;
      forceJiraTicket?: boolean;
      quoteId?: string;
    }) =>
      client
        .post(`customer/${customerId}/named-insured-update`, {
          json: {
            newName,
            ...(forceJiraTicket ? { forceJiraTicket: true } : {}),
            ...(quoteId ? { quoteId } : {}),
          },
        })
        .json<CreateNamedInsuredUpdateResult>(),
    onSuccess: (_, { customerId, newName, quoteId }) => {
      queryClient.invalidateQueries({
        queryKey: [...customerQueryKey._customer, customerId, 'named-insured-update-request'],
      });
      queryClient.invalidateQueries({
        queryKey: customerQueryKey.getCustomer(customerId),
      });
      if (quoteId && quoteStore) {
        const { structuredQuote, quoteItemTypeCodeNameToQuoteType } = quoteStore.getState();
        if (structuredQuote) {
          // Patch the quoteItems React Query cache so updateQuoteDetails picks up the new
          // name without refetching (which would reset unsaved form edits).
          const namedInsuredItem = structuredQuote.quoteItems.find(
            (qi) => qi.quoteItemType.codeName === CodeName.NamedInsured,
          );
          const customerNameAttrType =
            namedInsuredItem && quoteItemTypeCodeNameToQuoteType[namedInsuredItem.quoteItemType.id]?.['CustomerName'];
          if (namedInsuredItem && customerNameAttrType) {
            const cacheKey = quoteItemQueryKey.searchWithParams({ quoteId });
            const cached = queryClient.getQueryData<QuoteItemSearchResponse>(cacheKey);
            if (cached) {
              queryClient.setQueryData<NonNullable<QuoteItemSearchResponse>>(cacheKey, {
                ...cached,
                items: cached.items?.map((qi) => {
                  if (qi.id !== namedInsuredItem.id) return qi;
                  return {
                    ...qi,
                    quoteItemAttributes: qi.quoteItemAttributes.map((qia) =>
                      qia.quoteItemAttributeTypeId === customerNameAttrType.id ? { ...qia, value: newName } : qia,
                    ),
                  };
                }),
              });
            }
          }
        }
        queryClient.invalidateQueries({
          queryKey: quoteQueryKey.get(quoteId),
        });
      }
    },
  });

  return {
    createNamedInsuredUpdate: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};

export const usePatchNamedInsuredUpdateRequest = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ customerId, needsManualEndorsement }: { customerId: string; needsManualEndorsement: boolean }) =>
      client
        .patch(`customer/${customerId}/named-insured-update-request`, {
          json: { needsManualEndorsement },
        })
        .json<{ id: string }>(),
    onSuccess: (_, { customerId }) => {
      queryClient.invalidateQueries({
        queryKey: [...customerQueryKey._customer, customerId, 'named-insured-update-request'],
      });
    },
  });

  return {
    patchUpdateRequest: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
};
